import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { z } from "zod"
import {
  useDrilldownExpandableAt,
  useDrilldownMinWidth,
  useIsDrilldownOpen,
} from "~/components/design-system/pages/internal/InspectorDrilldownContext"
import { type InspectorWidth } from "~/components/design-system/pages/types"
import { useEventCallback } from "usehooks-ts"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"

type RegisteredInspector = {
  content: ReactNode
  defaultOpen: boolean
  width: InspectorWidth
  className?: string
}

// Grow-only resize: the registered width is the floor, capped at MAX_WIDTH_PX.
export const INSPECTOR_WIDTH_PX: Record<InspectorWidth, number> = {
  sm: 320,
  md: 400,
  lg: 512,
}
const DRILLDOWN_WIDTH_PX: Record<InspectorWidth, number> = {
  sm: 560,
  md: 680,
  lg: 760,
}
const MAX_WIDTH_PX = 900
// Old "side panel" wording kept deliberately: this is a live localStorage key holding each
// user's dragged width. Renaming the value resets everyone's inspector to the default.
const INSPECTOR_WIDTH_KEY = "module-side-panel-width"
const widthSchema = z.number()

type InspectorContextValue = {
  inspector: RegisteredInspector | null
  isOpen: boolean
  /** False when there's no toggle affordance and the inspector is forced open. */
  canToggle: boolean
  toggle: () => void
  open: () => void
  close: () => void
  registerInspector: (inspector: RegisteredInspector) => void
  unregisterInspector: () => void
  viewConfiguration: ReactNode | null
  registerViewConfiguration: (node: ReactNode) => void
  unregisterViewConfiguration: () => void
  /** Inspector header pinned above the inspector's views, so it survives a drilldown. */
  nav: ReactNode | null
  registerNav: (node: ReactNode) => void
  unregisterNav: () => void
  /** Context band pinned above the tab row — what qualifies every figure in the inspector. */
  context: ReactNode | null
  registerContext: (node: ReactNode) => void
  unregisterContext: () => void
  /** Committed inspector width in px (clamped to the registered size as a grow-only minimum). */
  width: number
  /** Registered floor (grow-only) and ceiling for the width — used by the resize handle. */
  minWidth: number
  maxWidth: number
  /** Persist a new width (clamped). Called once on resize mouse-up, never mid-drag, so the
   * inspector/table only reflow after the drag ends. */
  commitWidth: (px: number) => void
  /** Whether the inspector is currently wide enough to show the level's extra content — by the expand
   * control OR by the user having dragged past its threshold. */
  isExpanded: boolean
  /** Toggle between showing and hiding the level's extra content. */
  toggleExpanded: () => void
  /** False once the inspector is already as wide as it can get, so the control can hide instead
   * of becoming a no-op. */
  canExpand: boolean
  /** Portal target for `InspectorFooter`, pinned below the inspector's views. */
  footerEl: HTMLElement | null
  setFooterEl: (el: HTMLElement | null) => void
}

/**
 * The callbacks alone, in a context of their own with a permanently stable value.
 *
 * Registering content is a WRITE, and a writer must not be a reader: a page body that reads the
 * full context (via `useOpenInspector`, say) and also renders its own `inspector`/`viewConfiguration`
 * node would loop forever — the node has a new identity each render, so registering it changes the
 * state below, which re-renders the body, which builds a new node. Readers of this context never
 * re-render, so the cycle cannot close.
 */
type InspectorActions = Pick<
  InspectorContextValue,
  | "toggle"
  | "open"
  | "close"
  | "registerInspector"
  | "unregisterInspector"
  | "registerViewConfiguration"
  | "unregisterViewConfiguration"
  | "registerNav"
  | "unregisterNav"
  | "registerContext"
  | "unregisterContext"
>

const InspectorActionsContext = createContext<InspectorActions | null>(null)

const InspectorContext = createContext<InspectorContextValue | null>(null)

export const InspectorProvider = ({
  children,
  hasTabs = false,
}: {
  children: ReactNode
  hasTabs?: boolean
}) => {
  const [inspector, setInspector] = useState<RegisteredInspector | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  // In a ref (not state) so the callbacks below keep stable identity; gates the default-open state.
  const hasUserToggled = useRef(false)
  const [viewConfiguration, setViewConfiguration] = useState<ReactNode | null>(null)
  const [nav, setNav] = useState<ReactNode | null>(null)
  const [context, setContext] = useState<ReactNode | null>(null)
  const [footerEl, setFooterEl] = useState<HTMLElement | null>(null)
  // 0 = unset → falls back to the registered min width.
  const [storedWidth, setStoredWidth] = useSafeLocalStorage(
    INSPECTOR_WIDTH_KEY,
    0,
    widthSchema,
    false,
  )
  // Which drilldown level the user expanded, or null. Keyed by level rather than a bare boolean so
  // the expansion cannot outlive its level: popping it, or opening a different one, drops it.
  // Deliberately NOT persisted and not folded into `storedWidth` — expanding is a
  // look-at-this-now gesture, so collapsing returns the width the user had. Dragging the seam
  // while expanded is the one thing that replaces it: that is a deliberate resize, not a nudge.
  const [expandedLevelKey, setExpandedLevelKey] = useState<string | null>(null)
  // The toggle lives in the VC toolbar when a VC exists, otherwise as a fallback in the
  // tabs row. With neither there's no affordance, so force the inspector open rather than
  // leaving it stuck closed.
  const hasToggleAffordance = !!viewConfiguration || hasTabs
  const isInspectorOpen = inspector ? (hasToggleAffordance ? isOpen : true) : false

  // Stable identities: useRegisterInspector lists these in effect deps, so if they changed each
  // render the effect would thrash the inspector (unregister→register), remounting all cards.
  const toggle = useCallback(() => {
    hasUserToggled.current = true
    setIsOpen((prev) => !prev)
  }, [])
  const open = useCallback(() => {
    hasUserToggled.current = true
    setIsOpen(true)
  }, [])
  const close = useCallback(() => {
    hasUserToggled.current = true
    setIsOpen(false)
  }, [])
  const registerInspector = useCallback((next: RegisteredInspector) => {
    // Update in place — flipping through null would unmount/remount the inspector (the "reload").
    setInspector(next)
    if (!hasUserToggled.current) setIsOpen(next.defaultOpen)
  }, [])
  const unregisterInspector = useCallback(() => setInspector(null), [])
  const registerViewConfiguration = useCallback((node: ReactNode) => setViewConfiguration(node), [])
  const unregisterViewConfiguration = useCallback(() => setViewConfiguration(null), [])
  const registerNav = useCallback((node: ReactNode) => setNav(node), [])
  const unregisterNav = useCallback(() => setNav(null), [])
  const registerContext = useCallback((node: ReactNode) => setContext(node), [])
  const unregisterContext = useCallback(() => setContext(null), [])

  // A drilldown level can ask for more room than the inspector's registered size; the deepest ask
  // wins, and it is released when that level pops. The user's stored width still wins when larger.
  const drilldownMinWidth = useDrilldownMinWidth()
  const drilldownOpen = useIsDrilldownOpen()
  // No inspector means no drilldown either (ModuleInspector closes the stack), so one guard covers
  // both.
  const minWidth = inspector
    ? Math.max(
        drilldownOpen ? DRILLDOWN_WIDTH_PX[inspector.width] : INSPECTOR_WIDTH_PX[inspector.width],
        drilldownMinWidth,
      )
    : 0
  const maxWidth = Math.min(
    typeof window !== "undefined" ? window.innerWidth * 0.7 : MAX_WIDTH_PX,
    MAX_WIDTH_PX,
  )
  const storedOrFloor = Math.max(minWidth, Math.min(maxWidth, storedWidth))

  // The resize handle previews the new width with a ghost line while dragging and calls this
  // once on mouse-up — so the inspector (and the table beside it) only reflow after the drag ends.
  // A drag is a more specific intent than "make it big", so it ends the expanded state and the
  // dragged px becomes the user's width.
  const commitWidth = useCallback(
    (px: number) => {
      setExpandedLevelKey(null)
      setStoredWidth(Math.round(Math.max(minWidth, Math.min(maxWidth, px))))
    },
    [minWidth, maxWidth, setStoredWidth],
  )

  // The level on show: the width at which it reveals extra content (0 when it has none) and its
  // key, so an expansion can be tied to the exact level that asked for it.
  const { expandableAt, levelKey } = useDrilldownExpandableAt()
  // An expansion holds only while the level that owns it is the one on show and the inspector is open.
  // Derived rather than reset in an effect, so popping the level releases the width in the same
  // render, and a different drilldown never inherits an inspector widened for something else. The key
  // is the level's stable `useId()`, so returning to the SAME level restores the expansion — a
  // planner who backs out to check something shouldn't lose their wide table.
  const expanded = expandedLevelKey !== null && expandedLevelKey === levelKey && isInspectorOpen
  // What the inspector actually renders at. Expanding never writes here — it is dropped on collapse,
  // on leaving the level, and by any drag (`commitWidth`), which stores the px the user dragged to.
  const width = expanded ? maxWidth : storedOrFloor
  // The control toggles the CONTENT state, so it is offered whenever the level has extra content
  // and the inspector can actually reach BOTH sides of the threshold. A narrow viewport can put
  // `maxWidth` below it, and a level whose `minWidth` floor already exceeds its own threshold can
  // never go back to narrow — in either case a toggle would dead-end, which is worse than none.
  const canExpand = expandableAt > 0 && maxWidth >= expandableAt && minWidth < expandableAt
  // Wide by either route: the control expanded it, or the user already dragged past the threshold.
  const isWide = width >= expandableAt && expandableAt > 0

  // Collapsing an inspector the user dragged wide has to write a narrower stored width, since there is
  // no expansion to drop — they pressed collapse, so a narrower inspector is what they asked for.
  const toggleExpanded = useEventCallback(() => {
    if (!isWide) {
      setExpandedLevelKey(levelKey)
      return
    }
    if (expanded) setExpandedLevelKey(null)
    else commitWidth(expandableAt - 1)
  })

  const actions = useMemo<InspectorActions>(
    () => ({
      toggle,
      open,
      close,
      registerInspector,
      unregisterInspector,
      registerViewConfiguration,
      unregisterViewConfiguration,
      registerNav,
      unregisterNav,
      registerContext,
      unregisterContext,
    }),
    [
      toggle,
      open,
      close,
      registerInspector,
      unregisterInspector,
      registerViewConfiguration,
      unregisterViewConfiguration,
      registerNav,
      unregisterNav,
      registerContext,
      unregisterContext,
    ],
  )

  const value = useMemo<InspectorContextValue>(
    () => ({
      inspector,
      isOpen: isInspectorOpen,
      canToggle: hasToggleAffordance,
      toggle,
      open,
      close,
      registerInspector,
      unregisterInspector,
      viewConfiguration,
      registerViewConfiguration,
      unregisterViewConfiguration,
      nav,
      registerNav,
      unregisterNav,
      context,
      registerContext,
      unregisterContext,
      width,
      minWidth,
      maxWidth,
      commitWidth,
      isExpanded: isWide,
      toggleExpanded,
      canExpand,
      footerEl,
      setFooterEl,
    }),
    [
      inspector,
      isInspectorOpen,
      hasToggleAffordance,
      viewConfiguration,
      nav,
      toggle,
      open,
      close,
      registerInspector,
      unregisterInspector,
      registerViewConfiguration,
      unregisterViewConfiguration,
      registerNav,
      unregisterNav,
      context,
      registerContext,
      unregisterContext,
      width,
      minWidth,
      maxWidth,
      commitWidth,
      isWide,
      toggleExpanded,
      canExpand,
      footerEl,
    ],
  )

  return (
    <InspectorActionsContext.Provider value={actions}>
      <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>
    </InspectorActionsContext.Provider>
  )
}

const useInspectorActions = () => {
  const ctx = useContext(InspectorActionsContext)
  if (!ctx) {
    throw new Error(
      "useInspectorActions must be used inside a <Module>. A Page archetype was rendered outside of <Module>.",
    )
  }
  return ctx
}

export const useInspectorContext = () => {
  const ctx = useContext(InspectorContext)
  if (!ctx) {
    throw new Error(
      "useInspectorContext must be used inside a <Module>. A Page archetype was rendered outside of <Module>.",
    )
  }
  return ctx
}

/**
 * Whether the current page registered an inspector. For controls that only make sense when there
 * is one to reveal — an in-cell peek button, say — and that keep their old affordance otherwise.
 */
export const useHasInspector = () => !!useInspectorContext().inspector

/**
 * Programmatically open the current page's inspector; a no-op when already open.
 *
 * For "selection drives the inspector" flows: call it in a row's click handler so the detail is
 * visible even if the user previously collapsed it. Only ever opens, never closes, so the toolbar
 * toggle keeps working.
 *
 * Reads the actions context, so a page body can call it without re-rendering whenever the inspector
 * state changes — see {@link InspectorActions}.
 */
export const useOpenInspector = () => {
  const { open } = useInspectorActions()
  return open
}

/**
 * Programmatically collapse the current page's inspector; a no-op when already closed.
 *
 * The counterpart to {@link useOpenInspector} for flows where dropping the selection should also
 * hide the detail, rather than leave the inspector showing its no-selection state.
 */
export const useIsInspectorOpen = () => useInspectorContext().isOpen

export const useCloseInspector = () => {
  const { close } = useInspectorActions()
  return close
}

/**
 * The inspector's live width, for drilldown content that shows more when there is room.
 *
 * Branch on `width` against a threshold of your own, NOT on `isExpanded` — `width` reflects the
 * expand control AND a manual drag of the resize handle, so both affordances reveal the same
 * thing. `isExpanded` is there for copy that names the control.
 *
 * Pair it with `expandable` on the `<InspectorDrilldown>` that hosts the content, or the extra
 * detail is reachable only by dragging.
 *
 * Outside a `<Module>` it reports width 0 rather than throwing, so a component that is width-aware
 * in an inspector can still be rendered in a dialog or on a page — it just never reads as wide.
 *
 * @example
 * const { width } = useInspectorWidth()
 * const isWide = width >= WIDE_ENOUGH_FOR_YEAR_COLUMNS_PX
 */
export const useInspectorWidth = () => {
  const ctx = useContext(InspectorContext)
  return {
    width: ctx?.width ?? 0,
    maxWidth: ctx?.maxWidth ?? 0,
    isExpanded: ctx?.isExpanded ?? false,
  }
}

export const useRegisterInspector = (
  content: ReactNode | undefined,
  defaultOpen: boolean,
  width: InspectorWidth,
  className?: string,
) => {
  const { registerInspector, unregisterInspector } = useInspectorActions()
  // Update in place; do NOT unregister on a content change — content gets a new identity each
  // render, so unregister→register would remount all inspector cards. Clear only on real unmount.
  useEffect(() => {
    if (content === undefined) {
      unregisterInspector()
      return
    }
    registerInspector({ content, defaultOpen, width, className })
  }, [content, defaultOpen, width, className, registerInspector, unregisterInspector])

  useEffect(() => unregisterInspector, [unregisterInspector])
}

/**
 * Hand the inspector's pinned header (a `<InspectorNav>`) to the inspector chrome, so it stays above the
 * views instead of scrolling away with the content behind a drilldown.
 *
 * Same in-place update discipline as {@link useRegisterInspector}: the callbacks are stable, so a
 * content change re-registers rather than unmount/remounting the header.
 */
export const useRegisterInspectorNav = (content: ReactNode | undefined) => {
  const { registerNav, unregisterNav } = useInspectorActions()
  useEffect(() => {
    if (content === undefined) {
      unregisterNav()
      return
    }
    registerNav(content)
  }, [content, registerNav, unregisterNav])

  useEffect(() => unregisterNav, [unregisterNav])
}

/**
 * Registers the context band the chrome pins above the tab row. Same contract as
 * {@link useRegisterInspectorNav}: pass a STABLE node, since the effect keys off identity.
 */
export const useRegisterInspectorContext = (content: ReactNode | undefined) => {
  const { registerContext, unregisterContext } = useInspectorActions()
  useEffect(() => {
    if (content === undefined) {
      unregisterContext()
      return
    }
    registerContext(content)
  }, [content, registerContext, unregisterContext])

  useEffect(() => unregisterContext, [unregisterContext])
}

export const useRegisterViewConfiguration = (content: ReactNode | undefined) => {
  const { registerViewConfiguration, unregisterViewConfiguration } = useInspectorActions()
  useEffect(() => {
    if (content === undefined) {
      unregisterViewConfiguration()
      return
    }
    registerViewConfiguration(content)
    return () => unregisterViewConfiguration()
  }, [content, registerViewConfiguration, unregisterViewConfiguration])
}
